import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  IconButton,
  Skeleton,
  Slide,
  Grid,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Style Sheet File
import { manualEntryStyles } from "./manualEntryStyles";
import "./manualEntryStyles.css";

// Imported Images and Icon
import {
  UseGroupKpis,
  UseGroupAllKpis,
  UseKpiScoresTable,
  useGetPreviousKpis,
  filterKpisByCreationDate,
  filterKpisByUserScores,
  isExcludedFromBoards,
} from "../../hooks/kpi";
import { usePastMonthKpisQuery } from "../../hooks/usePastMonthKpi";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { UseSelectedWorkspace } from "../../hooks/company";
import { UseCompanyId } from "../../hooks/auth";
import { companyMonthStr } from "../../helpers/companyTime";
import SoftBox from "../../components/SoftBox";
import ManualEntryTable from "../../components/ManualEntry/Table";
import PageHeader from "../../components/PageHeader";
import Filter from "../../components/ManualEntry/Filter";
import SoftButton from "../../components/SoftButton";
import { ReactComponent as FilterIcon } from "../../img/scorekeeper-filter.svg";
import { useNavigate, useSearchParams } from "react-router-dom";

// import SoftTypography from "../../components/SoftTypography";
// import SoftInput from "../../components/SoftInput";
// import { ReactComponent as BellIcon } from "../../img/scorekeeper-bell.svg";
// import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
// import SearchBar from "../../components/SearchBar";
// import SendKudos from "../../components/ManualEntry/SendKudos";
// import ScoreSettings from "../../components/ManualEntry/ScoreSettings";
// import moment from "moment";


export default function ManualEntry() {
  const classes = manualEntryStyles();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertBoxText, setAlertBoxText] = useState("");
  const [monthRef, setMonthRef] = useState(null);
  const [filterIndex, setFilterIndex] = useState(0);
  const [pastMonth, setPastMonth] = useState(false);
  const [searchParams] = useSearchParams();

  const groupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const currentWorkspace = UseSelectedWorkspace();
  const companyId = UseCompanyId();
  const usersKpis = UseKpiScoresTable(monthRef, currentWorkspace?._id);
  const { getPreviousKpis } = useGetPreviousKpis();
  const navigate = useNavigate();
  const kpiParam = searchParams.get("kpi");
  const monthToday = companyMonthStr();
  const monthKey = monthRef?.format("YYYY-MM") ?? null;
  const isPastMonthView = !!monthKey && monthKey < monthToday;

  // Past-month data path: TanStack Query fetches scores independently of Redux.
  // On success it dispatches GET_PREVIOUS_KPIS_SUCCESS so UseKpiScoresTable
  // and displayedGroupKpis can read from Redux without any other changes.
  usePastMonthKpisQuery(
    isPastMonthView ? monthKey : null,
    currentWorkspace?._id,
    companyId
  );

  // Get historic user KPIs for filtering past month metrics (columns)
  const historicKpis = useSelector((state) => {
    if (monthRef) {
      const mk = monthRef.format("YYYY-MM");
      return state.kpiReducer.historicKpis?.[mk];
    }
    return null;
  });

  // Compute filtered KPIs to display
  const displayedGroupKpis = React.useMemo(() => {
    // "display" metrics (Team Hours, Total Revenue, ...) are ungamified,
    // company/workspace-wide rollups with no player behind the number — never
    // meant to appear as an editable per-player column here, current month
    // included. isExcludedFromBoards covers this too, but only the past-month
    // branch below actually calls it — applied up front so both branches
    // agree instead of current-month silently letting it through.
    if (!monthRef) {
      return (groupKpis || []).filter((kpi) => !isExcludedFromBoards(kpi, monthToday));
    }

    const mk = monthRef.format("YYYY-MM");
    if (mk === monthToday) {
      return filterKpisByCreationDate(groupKpis, monthRef)
        .filter((kpi) => !isExcludedFromBoards(kpi, mk));
    } else {
      // Past months: filter by creation date, user scores, and exclude retired
      // metrics. historicKpis is populated by TanStack query's onSuccess.
      const creationFiltered = filterKpisByCreationDate(groupAllKpis, monthRef);
      return filterKpisByUserScores(creationFiltered, historicKpis)
        .filter((kpi) => !isExcludedFromBoards(kpi, mk));
    }
  }, [monthRef, groupKpis, groupAllKpis, historicKpis, monthToday]);

  const theme = useTheme();
  const {
    primary: { main: primaryColor, light: brighterPrimaryColor },
  } = theme.palette;

  const handleApply = (index) => {
    setFilterIndex(index);
  };

  useEffect(() => {
    if (alertOpen) {
      setTimeout(() => {
        setAlertOpen(false);
      }, 2000);
    }
  }, [alertOpen]);

  React.useEffect(() => {
    if (monthRef) {
      const mk = monthRef.format("YYYY-MM");
      if (mk === monthToday) {
        setPastMonth(false);
        // Current month selected: use existing Redux fetch path
        getPreviousKpis(monthRef);
      } else {
        setPastMonth(true);
        // Past months are fetched by usePastMonthKpisQuery above — no Redux
        // dispatch needed here
      }
    } else {
      setPastMonth(false);
    }
  }, [monthRef, currentWorkspace, monthToday]);

  const topUser = Array.isArray(usersKpis) && usersKpis.length > 0
    ? usersKpis.reduce((maxObject, currentObject) => {
        if (currentObject.total > maxObject.total) {
          return currentObject;
        }
        return maxObject;
      }, usersKpis[0])
    : null;

  return (
    <>
      <PageHeader
        headerText="Scorekeeper"
        subheaderText={`Manage Player Scores and Performance in the ${currentWorkspace?.name} workspace`}
        onBack={() => navigate(-1)}
        inlineBack
      />

     {/*  <SoftBox
        sx={{
          marginX: { mini: "1rem", md: "1.5rem" },
          marginY: "2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SoftBox>
          <SoftTypography variant="h5" color="black">
            Technicians
          </SoftTypography>
          <SoftTypography variant="body2" color="black">
            The score of players in the technicians workspace
          </SoftTypography>
        </SoftBox>
        <SoftBox display="flex" gap="0.75rem" alignItems="center">
          <BellIcon />
          <SendKudos topUser={topUser}>
            <SoftButton
              variant="contained"
              color="white"
              size="small"
              sx={{ textTransform: "none", padding: "13px 19px" }}
            >
              Click Here
            </SoftButton>
          </SendKudos>
        </SoftBox>
      </SoftBox> */}

      <SoftBox
        sx={{
          marginX: { mini: "1rem", md: "1.5rem" },
          marginTop: { mini: "10px", md: 0 },
          marginBottom: { mini: "1rem", md: "1.5rem" },
        }}
      >
        <Grid container>
          {alertOpen && (
            <Box className={classes.successAlert}>
              <Slide direction="left" in={alertOpen}>
                <Alert
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setAlertOpen(false);
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                >
                  {alertBoxText}
                </Alert>
              </Slide>
            </Box>
          )}

          <Grid item mini={12} sx={{ minWidth: 0 }}>
            <SoftBox sx={{ position: "relative", minWidth: 0 }}>
              {/* Mobile-only scroll hint — the table's columns are wider than
                  a phone screen and rely on horizontal scroll; without this
                  it just looks cut off instead of scrollable. */}
              <SoftBox
                sx={{
                  display: { mini: "block", md: "none" },
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  width: "28px",
                  borderRadius: "0 1rem 1rem 0",
                  background: "linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.95))",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              <SoftBox
                sx={{
                  overflowX: "auto",
                  width: "100%",
                  minWidth: 0,
                  // Setting only overflowX makes the browser compute
                  // overflow-y as "auto" too (CSS spec: an axis left at its
                  // "visible" default gets forced to "auto" once the other
                  // axis is non-visible) — this box quietly became its own
                  // vertical scroll container as well. That gave it a
                  // scrollbar-corner where dragging the horizontal thumb
                  // could also nudge this box's own vertical scroll, moving
                  // the sticky table header independently of the page's
                  // real scroll position. Pin it to "visible" so this box
                  // only ever scrolls sideways; vertical scrolling stays
                  // owned by the page.
                  overflowY: "visible",
                  background: "white",
                  borderRadius: "1rem",
                }}
              >
              <SoftBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "1rem",
                  // Pin the date/filter row to the left edge of the table's
                  // horizontal scroll container (set up above) so it stays
                  // visible while scrolling sideways through wide columns,
                  // instead of sliding off with the rest of the content.
                  position: "sticky",
                  left: 12,
                  zIndex: 2,
                  width: "fit-content",
                  background: "white",
                }}
              >
                <SoftBox sx={{ display: "flex", gap: "0.5rem" }}>
                  <SoftBox>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        openTo="month"
                        views={["year", "month"]}
                        inputFormat="MM/YYYY"
                        label=""
                        minDate={new Date("2020-01-01")}
                        maxDate={new Date()}
                        value={monthRef}
                        onChange={(newValue) => {
                          setMonthRef(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            placeholder="Last 30 days"
                            {...params}
                            helperText={null}
                          />
                        )}
                      />
                    </LocalizationProvider>
                    {/* <SearchBar
                      placeholder="Last 30 days"
                      icon={{
                        component: (
                          <EventOutlinedIcon sx={{ fill: "#6C757D" }} />
                        ),
                        direction: "right",
                      }}
                      sx={{
                        "&.Mui-focused": {
                          borderColor: primaryColor,
                          boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                          outline: 0,
                        },
                      }}
                    /> */}
                  </SoftBox>
                  <SoftBox>
                    <Filter handleApply={handleApply}>
                      <SoftButton
                        variant="outlined"
                        sx={{
                          color: "#495057",
                          borderColor: "#E9ECEF",
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "10px 29px",
                          boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <FilterIcon sx={{ marginRight: "0.5rem" }} /> Filter
                      </SoftButton>
                    </Filter>
                  </SoftBox>
                </SoftBox>
              </SoftBox>

              <Divider />
              <ManualEntryTable
                key={monthKey ?? "live"}
                groupKpis={displayedGroupKpis}
                usersKpis={usersKpis}
                monthRef={monthRef}
                filterIndex={filterIndex}
                isSelected={kpiParam}
              />
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
}
